import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import LeegoCopilot from "@/components/LeegoCopilot";
import QuestionnaireDialog from "@/components/QuestionnaireDialog";
import DecisionsDialog from "@/components/DecisionsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, CreditCard, FileSearch, Gavel, LogOut, Search, Scale, ShieldCheck } from "lucide-react";

export default function LegalWorkspace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [query, setQuery] = useState("");
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchedFor, setSearchedFor] = useState("");
  const requestId = useRef(0);
  const [selected, setSelected] = useState(null);
  const [topic, setTopic] = useState(null);
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const [leegoPrompt, setLeegoPrompt] = useState(null);
  const [jurisdictionScope, setJurisdictionScope] = useState("federal");
  const [stateOptions, setStateOptions] = useState([]);
  const [stateCode, setStateCode] = useState("");
  const scopeLabel = jurisdictionScope === "federal" ? "Federal" : "State / Local";

  const courtHelp = jurisdictionScope === "state_local" ? stateOptions.find((s) => s.code === stateCode) : null;

  useEffect(() => {
    api.get("/questionnaires", { params: { jurisdiction_scope: jurisdictionScope } })
      .then((r) => setTopics(r.data || [])).catch(() => {});
  }, [jurisdictionScope]);

  useEffect(() => {
    if (jurisdictionScope !== "state_local" || stateOptions.length > 0) return;
    api.get("/states").then((r) => setStateOptions(r.data || [])).catch(() => {});
  }, [jurisdictionScope, stateOptions.length]);

  const errorText = (err) =>
    err?.response?.data?.detail && typeof err.response.data.detail === "string"
      ? err.response.data.detail
      : "Search failed. Check your connection and try again.";

  // Every load and search takes a ticket; a response that arrives after a newer
  // request started is ignored, so old results never replace new ones.
  useEffect(() => {
    const id = ++requestId.current;
    const params = { jurisdiction_scope: jurisdictionScope };
    if (jurisdictionScope === "state_local" && stateCode) params.state = stateCode;
    setSearchError("");
    setSearchedFor("");
    api.get("/regulations", { params })
      .then((r) => { if (id === requestId.current) setRegulations((r.data || []).slice(0, 12)); })
      .catch((err) => {
        if (id !== requestId.current) return;
        setRegulations([]);
        setSearchError(errorText(err));
      });
  }, [jurisdictionScope, stateCode]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return;
    const id = ++requestId.current;
    setLoading(true);
    setSearchError("");
    try {
      const params = { q, jurisdiction_scope: jurisdictionScope };
      if (jurisdictionScope === "state_local" && stateCode) params.state = stateCode;
      const r = await api.get("/regulations", { params });
      if (id !== requestId.current) return;
      const rows = r.data || [];
      setRegulations(rows);
      setSelected(rows[0] || null);
      setSearchedFor(q);
    } catch (err) {
      if (id !== requestId.current) return;
      setRegulations([]);
      setSelected(null);
      setSearchedFor("");
      setSearchError(errorText(err));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const matterContext = useMemo(() => {
    const parts = [];
    if (query) parts.push(`Current legal research query: ${query}`);
    if (selected) {
      parts.push(`Selected authority: ${selected.citation || ""} — ${selected.title || ""}`);
      if (selected.text) parts.push(`Authority text: ${selected.text.slice(0, 6000)}`);
      if (selected.source_url) parts.push(`Official source: ${selected.source_url}`);
    }
    if (topic?.label) parts.push(`Current matter type: ${topic.label}`);
    return parts.join("\n\n");
  }, [query, selected, topic]);

  const changeScope = (scope) => {
    if (scope === jurisdictionScope) return;
    setSelected(null);
    setRegulations([]);
    setTopic(null);
    if (scope === "federal") setStateCode("");
    setJurisdictionScope(scope);
  };

  const openAssessment = (t) => {
    setTopic(t);
    setQuestionnaireOpen(true);
  };

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 shrink-0 border-b border-border bg-[#0B1120] px-5 flex items-center gap-4">
          <Brand size="sm" />
          <div className="h-6 w-px bg-border" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold">Legal Workspace</div>
            <div className="text-[10px] text-muted-foreground">Research, authority, matter assessment, and drafting</div>
          </div>
          <div className="flex border border-border" role="group" aria-label="Legal jurisdiction">
            {[
              ["federal", "Federal"],
              ["state_local", "State / Local"],
            ].map(([value, label]) => (
              <button key={value} type="button" onClick={() => changeScope(value)}
                className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${jurisdictionScope === value ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
                aria-pressed={jurisdictionScope === value}>{label}</button>
            ))}
          </div>
          {jurisdictionScope === "state_local" && (
            <select
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              data-testid="jurisdiction-state-select"
              className="text-[11px] bg-secondary border border-border px-2 py-1.5 text-foreground"
            >
              <option value="">Which state?</option>
              {stateOptions.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          )}
          <button onClick={() => navigate("/pricing")} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" />{user?.tier || "free"}</button>
          <button onClick={() => { logout(); navigate("/"); }} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" />Sign out</button>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-5 py-6 space-y-6">
            <section className="border border-border bg-card">
              <div className="p-5 border-b border-border flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 text-[10px] font-mono-tech text-primary border border-primary/30 bg-primary/5 px-2 py-1"><ShieldCheck className="w-3 h-3" />{scopeLabel.toUpperCase()} · GROUNDED LEGAL RESEARCH</div>
                  <h1 className="font-serif-display text-3xl font-semibold mt-3">Work the matter. Leego Beego stays beside you.</h1>
                  <p className="text-sm text-muted-foreground mt-2 max-w-3xl">Search HardKnockLegal authority, review official source text, run a case assessment, and ask Leego Beego questions without leaving the workspace.</p>
                </div>
                <div className="hidden md:flex w-12 h-12 border border-primary/30 bg-primary/5 items-center justify-center"><Scale className="w-5 h-5 text-primary" /></div>
              </div>

              <form onSubmit={runSearch} className="p-5 flex gap-2">
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${scopeLabel.toLowerCase()} regulations, procedure, deadlines, retaliation…`} className="rounded-none bg-secondary border-border" />
                <Button type="submit" disabled={loading} className="rounded-none px-5"><Search className="w-4 h-4 mr-2" />Search</Button>
                <Button type="button" variant="outline" onClick={() => setDecisionsOpen(true)} className="rounded-none"><Gavel className="w-4 h-4 mr-2" />Case Law</Button>
              </form>
              {jurisdictionScope === "state_local" && (
                <div className="px-5 pb-5 -mt-2 text-xs text-muted-foreground" data-testid="state-court-help">
                  {courtHelp ? (
                    <>Official links for {courtHelp.name}:{" "}
                      <a href={courtHelp.courts_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">court self-help, forms &amp; local rules</a>{" · "}
                      <a href={courtHelp.statutes_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">state laws</a></>
                  ) : "Choose your state to load its filing deadlines, fault rules and official court self-help links."}
                </div>
              )}
            </section>

            <section className="grid lg:grid-cols-[340px_1fr] gap-4">
              <div className="border border-border bg-card min-h-[430px]">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /><span className="text-sm font-semibold">Authority results</span></div>
                <div className="divide-y divide-border max-h-[560px] overflow-y-auto">
                  {searchError ? (
                    <div className="p-4 text-sm text-destructive" role="alert" data-testid="authority-search-error">{searchError}</div>
                  ) : regulations.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground" data-testid="authority-empty">
                      {searchedFor ? `No indexed authority matched "${searchedFor}". Try different or broader terms.` : "Search for a legal issue to load authority."}
                    </div>
                  ) : regulations.map((r, i) => (
                    <button key={`${r.citation}-${i}`} onClick={() => setSelected(r)} className={`w-full text-left p-4 transition-colors ${selected?.citation === r.citation ? "bg-primary/10" : "hover:bg-secondary/60"}`}>
                      <div className="text-xs font-semibold text-primary">{r.citation}</div>
                      <div className="text-sm mt-1 line-clamp-2">{r.title}</div>
                      <div className="text-[10px] text-muted-foreground mt-2">{r.state ? `${r.state} · ` : ""}{r.branch || `${scopeLabel} authority`}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-border bg-card min-h-[430px]">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2"><FileSearch className="w-4 h-4 text-primary" /><span className="text-sm font-semibold">Authority viewer</span></div>
                {selected ? (
                  <article className="p-5">
                    <div className="text-xs font-mono-tech text-primary">{selected.citation}</div>
                    <h2 className="font-serif-display text-2xl font-semibold mt-2">{selected.title}</h2>
                    <div className="mt-5 whitespace-pre-wrap text-sm leading-7 text-foreground/90">{selected.text}</div>
                    {selected.source_url && <a href={selected.source_url} target="_blank" rel="noreferrer" className="inline-flex mt-5 text-xs text-primary hover:underline">Open official source</a>}
                  </article>
                ) : (
                  <div className="p-8 h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                    <BookOpen className="w-8 h-8 mb-3 opacity-50" />
                    <div className="text-sm">Select an authority result to review it here.</div>
                    <div className="text-xs mt-1">Leego Beego receives the selected authority as workspace context.</div>
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold">Matter assessments</div>
                  <div className="text-xs text-muted-foreground mt-1">Answer focused questions, then continue the analysis with Leego Beego.</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {topics.map((t) => (
                  <button key={t.key} onClick={() => openAssessment(t)} className="text-left border border-border bg-card hover:border-primary/50 p-4 transition-colors">
                    <div className="text-xs font-semibold">{t.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">Open guided assessment</div>
                  </button>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>

      <LeegoCopilot matterContext={matterContext} topic={topic?.key || null} jurisdictionScope={jurisdictionScope} stateCode={stateCode} prompt={leegoPrompt} onPromptConsumed={() => setLeegoPrompt(null)} />
      <QuestionnaireDialog open={questionnaireOpen} onOpenChange={setQuestionnaireOpen} topic={topic} onComplete={(prompt) => setLeegoPrompt(prompt)} />
      <DecisionsDialog open={decisionsOpen} onOpenChange={setDecisionsOpen} onAsk={(prompt) => setLeegoPrompt(prompt)} jurisdictionScope={jurisdictionScope} stateCode={stateCode} />
    </div>
  );
}
